// src/components/Dashboard/RoleDashboard/RoleExtras/VSNWorld.jsx

import React, { useEffect, useState, useMemo } from "react";
import { apiFetch } from "../../../../apiFetch";
import "./VSNWorld.css";

/* ------------------------------------------------------------------ */
/* Channels are shared across roles (labels can vary by role if needed) */
/* ------------------------------------------------------------------ */

const CHANNELS = {
    vsnWorld: {
        id: "UCwXdRTjP4RO3TFNfEEafDKw",
        label: "VSN World",
    },
    trainingVideos: {
        id: "UC82ALhXKYfX4BtDrzm8oM3A",
        label: "Player Development Program",
    },
};

const VALID_ROLES = ["agent", "scout", "club"];

function normalizeRole(r) {
    const role = String(r || "").toLowerCase();
    return VALID_ROLES.includes(role) ? role : "agent";
}

const VSNWorld = ({ role: roleProp }) => {
    /* -------------------- Role -------------------- */

    const roleFromStorage =
        typeof window !== "undefined"
            ? localStorage.getItem("role") || localStorage.getItem("userRole")
            : null;

    const role = normalizeRole(roleProp || roleFromStorage || "agent");


    /* -------------------- State -------------------- */

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState("vsnWorld");

    /* -------------------- Derived -------------------- */

    const pageTitle = useMemo(() => {
        switch (role) {
            case "scout":
                return "VSN World";
            case "club":
                return "VSN World";
            case "agent":
            default:
                return "VSN World";
        }
    }, [role]);

    /* -------------------- Data Fetch -------------------- */

    const fetchVideosFromDB = async (channelId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetch(
                `/videos?channelId=${encodeURIComponent(channelId)}`
            );


            if (!response.ok) {
                const text = await response.text().catch(() => "");
                throw new Error(`Failed to load videos (${response.status}): ${text}`);
            }

            const data = await response.json();

            if (Array.isArray(data)) {
                setVideos(data);
            } else {
                setVideos([]);
                console.error("[VSNWorld] Unexpected format from /videos:", data);
            }
        } catch (err) {
            console.error("[VSNWorld] Failed to fetch videos:", err);
            setError("Failed to load videos.");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const channelId = CHANNELS[selectedTab]?.id;
        if (!channelId) return;

        fetchVideosFromDB(channelId);
    }, [selectedTab]);


    /* -------------------- UI -------------------- */

    return (
        <div className={`vsn-world role-vsn-world ${role}-vsn-world`}>
            <h2 className="vsn-world-title">{pageTitle}</h2>

            <div className="tab-toggle">
                {Object.entries(CHANNELS).map(([key, { label }]) => (
                    <button
                        key={key}
                        className={selectedTab === key ? "active" : ""}
                        onClick={() => setSelectedTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {loading && <p className="vsn-world-status">Loading...</p>}
            {error && <p className="vsn-world-error">{error}</p>}

            {!loading && !error && (
                <div className="video-list">
                    {videos.length === 0 ? (
                        <p className="vsn-world-status">No videos available.</p>
                    ) : (
                        videos.map((video, index) => (
                            <div key={index} className="video-card">
                                <div className="video-title">{video.title}</div>
                                <div className="video-description">
                                    {video.description}
                                </div>
                                <iframe
                                    className="video-player"
                                    src={video.embedUrl}
                                    title={video.title}
                                    allowFullScreen
                                />
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default VSNWorld;
